import { Group, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconInfoCircle } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { IntegrationInputHandler } from '@/components/integrations/IntegrationInputHandler/IntegrationInputHandler'
import { IntegrationListItem } from '@/components/integrations/IntegrationListItem/IntegrationListItem'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { EventDestination } from '@/types/deployment'
import { FormValues } from '../IntegrationSelection'
import { integrationConfig } from '../config'

type MilestoneIntegrationProps = {
  deviceId: string
  form: UseFormReturnType<FormValues>
  disabled?: boolean
  hasMilestoneCameraSelected: boolean
}

export const MilestoneIntegration = ({
  deviceId,
  form,
  disabled,
  hasMilestoneCameraSelected
}: MilestoneIntegrationProps) => {
  const isSelected = form.getValues().integrations.milestone.isSelected
  const isDisabled = disabled || !hasMilestoneCameraSelected

  return (
    <IntegrationListItem
      checkboxId="milestone"
      isSelected={isSelected}
      title={
        <FormattedMessage
          id={integrationConfig[EventDestination.Milestone].name}
        />
      }
      description={
        <FormattedMessage
          id={integrationConfig[EventDestination.Milestone].description}
        />
      }
      logoSrc={integrationConfig[EventDestination.Milestone].logo}
      showDetails={isSelected}
      contentWidth={600}
      // If integration is disabled and not selected, show a tooltip.
      // If integration is disabled and selected, show an error message. Allow to deselect it or go back and choose another camera.
      disabled={isDisabled && !isSelected}
      tooltip={
        !disabled && !hasMilestoneCameraSelected && !isSelected ? (
          <FormattedMessage id="integrations.milestone.camera.required" />
        ) : undefined
      }
      onChange={(value) => {
        form.setFieldValue('integrations.milestone.isSelected', value)
        form.clearErrors()
      }}
    >
      <>
        {!disabled && !hasMilestoneCameraSelected ? (
          <Group wrap="nowrap" align="center" gap="xs" mb="md">
            <IconInfoCircle size={14} color="var(--mantine-color-red-6)" />

            <Text size="xs" c="red.6">
              <FormattedMessage id="integrations.milestone.camera.required" />
            </Text>
          </Group>
        ) : (
          <PlatformAlert variant="info" mb="xl">
            <Text size="xs">
              <FormattedMessage
                id="integrations.milestone.info"
                values={{
                  b: (chunks) => <b>{chunks}</b>
                }}
              />
            </Text>
          </PlatformAlert>
        )}

        <IntegrationInputHandler
          {...form.getInputProps('integrations.milestone.endpointUrl')}
          disabled={isDisabled}
          label={<FormattedMessage id="integrations.milestone.inputLabel" />}
          description={
            <FormattedMessage
              id="integrations.milestone.inputHint"
              values={{
                url: 'http(s)://<aibridge host>:<aibridge port>/api/bridge/graphql'
              }}
            />
          }
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          deviceId={deviceId}
          source={EventDestination.Milestone}
          isTestButtonDisabled={
            !form.isValid(`integrations.milestone.endpointUrl`)
          }
        />
      </>
    </IntegrationListItem>
  )
}
